import { ExtendLogo, TypographyV3 } from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import type { ImageFormat } from '@/app/components/Image';
import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';

export interface InformationModalContentProps {
  readonly copy: MessageKey;
  readonly eyebrow?: MessageKey | undefined;
  readonly footnote: MessageKey;
  readonly heading: MessageKey;
  readonly imageFormat?: ImageFormat;
  readonly imageSrc: string;
  readonly imageAlt: MessageKey;
  readonly title: MessageKey;
}

export default function InformationModalContent({
  copy,
  eyebrow,
  footnote,
  heading,
  imageAlt,
  imageFormat = 'jpg',
  imageSrc,
  title,
}: InformationModalContentProps) {
  const t = useTranslations();

  return (
    <div data-cy="information-modal-content">
      {eyebrow && (
        <TypographyV3 className="hidden text-sm font-normal text-gray-400 italic md:block">
          <RichText
            messageKey={eyebrow}
            tValues={{
              ExtendLogo: () => (
                <ExtendLogo
                  className="ml-1.5 inline-block h-4 w-12 -translate-y-px"
                  title="Extend"
                />
              ),
            }}
          />
        </TypographyV3>
      )}
      <TypographyV3
        Element="h2"
        variant="h2"
        className="mb-8 hidden text-2xl font-normal tracking-tighter md:block md:pr-12"
      >
        <RichText messageKey={title} />
      </TypographyV3>
      <div className="flex flex-col items-center gap-0 md:gap-8 lg:flex-row lg:gap-6">
        <Image
          src={imageSrc}
          localizedAlt={imageAlt}
          format={imageFormat}
          alt="Logo"
          className="mb-5 w-full shrink-0 rounded-[20px] object-cover md:mb-0 md:block md:h-[240px] lg:h-[235px] lg:w-[386px]"
          width={386}
          height={235}
        />
        <div className="w-full px-1">
          {eyebrow && (
            <TypographyV3 className="text-sm font-normal text-gray-400 italic md:hidden">
              <RichText
                messageKey={eyebrow}
                tValues={{
                  ExtendLogo: () => (
                    <ExtendLogo
                      className="ml-1.5 inline-block h-4 w-12 -translate-y-px"
                      title="Extend"
                    />
                  ),
                }}
              />
            </TypographyV3>
          )}
          <TypographyV3
            Element="h2"
            variant="h2"
            className="mb-4 block text-xl font-normal md:hidden"
          >
            <RichText messageKey={title} />
          </TypographyV3>
          <TypographyV3 className="mb-3 text-base font-bold md:mb-2 md:text-xl md:font-normal">
            {t(heading)}
          </TypographyV3>
          <ul className="mb-4 list-outside list-disc space-y-0.5 pl-6 text-sm font-normal">
            <RichText messageKey={copy} />
          </ul>
        </div>
      </div>
      <div className="mt-8 mb-5 hidden h-px w-full bg-gray-300 md:block" />
      <TypographyV3 className="text-xs font-normal text-gray-400 italic">
        {t(footnote)}
      </TypographyV3>
    </div>
  );
}
